// The one mapping from an error to words a user reads (audit frontend A.5: 380 toast calls and
// six error shapes). Screens and notifications call these, never error.message directly.
import { ApiError, TransportError, UploadRefusedError } from "../api/errors";
import {
  InsufficientCreditsDetails,
  LimitDetails,
  RateLimitedDetails,
  ValidationDetails,
} from "../api/generated/zod";

/** "about 5 minutes": a limit's wait, rounded up to what a person plans with. */
function waitFor(seconds: number): string {
  if (seconds < 60) return "a minute";
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.ceil(minutes / 60);
  return `about ${hours} hour${hours === 1 ? "" : "s"}`;
}

/** The server's message, with what a limit's details add: when to retry, or the limit itself. */
function apiMessage(error: ApiError): string {
  if (error.code === "RATE_LIMITED") {
    const details = RateLimitedDetails.safeParse(error.details);
    if (!details.success) return error.message;
    return `${error.message} You can try again in ${waitFor(details.data.retry_after_seconds)}.`;
  }
  if (error.code === "INSUFFICIENT_CREDITS") {
    const details = InsufficientCreditsDetails.safeParse(error.details);
    if (!details.success) return error.message;
    const { required, available } = details.data;
    // In debt the balance is negative (a capture past the team's credits, ledger-refund decision
    // 8): what the team owes reads plainer than a negative count (Fable's O4 on #816).
    const has = available < 0 ? `the team owes ${-available}` : `the team has ${available}`;
    return `${error.message} It needs ${required} credits; ${has}.`;
  }
  if (error.code === "VALIDATION_FAILED") {
    // The fields' own messages say what to fix, such as "Grant this month or the next."
    const details = ValidationDetails.safeParse(error.details);
    if (!details.success || details.data.errors.length === 0) return error.message;
    const notes = new Set(details.data.errors.map((each) => each.message));
    return `${error.message} ${[...notes].join(" ")}`;
  }
  if (error.code.endsWith("_LIMIT_REACHED")) {
    const details = LimitDetails.safeParse(error.details);
    if (details.success) return `${error.message} The limit is ${details.data.limit}.`;
  }
  return error.message;
}

export function toUserMessage(error: unknown): string {
  // The contract makes the envelope's message user-safe (proposal 3.2).
  if (error instanceof ApiError) return apiMessage(error);
  if (error instanceof UploadRefusedError) return "The storage refused this upload. Try again.";
  if (error instanceof TransportError) {
    if (error.kind === "network") {
      return "Can't reach Created.ai. Check your connection and try again.";
    }
    if (error.status === 429 || error.status === 503) {
      return "Created.ai is busy right now. Try again in a moment.";
    }
    return "Created.ai sent an answer this page did not expect. Try again in a moment.";
  }
  return "Something went wrong on this page. Reload it to try again.";
}

/** The request id to quote to support, when the server sent one. */
export function errorReference(error: unknown): string | undefined {
  return error instanceof ApiError ? error.requestId : undefined;
}
